import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  UserCheck,
  X,
  Clock,
  ArrowUpDown,
  Phone,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WaitingListEntry {
  id: number;
  agencyId: number;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  dateAdded: string;
  priority: number;
  priorityLabel: string;
  notes: string | null;
  status: string;
  personId: number | null;
}

interface WaitingListResponse {
  data: WaitingListEntry[];
  meta: { total: number };
}

type StatusFilter = "all" | "waiting" | "contacted" | "admitted" | "removed";

// ---------------------------------------------------------------------------
// Priority / Status Badges
// ---------------------------------------------------------------------------

const PRIORITY_CONFIG: Record<string, { label: string; className: string }> = {
  urgent: {
    label: "Urgent",
    className:
      "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
  },
  high: {
    label: "High",
    className:
      "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300",
  },
  medium: {
    label: "Medium",
    className:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300",
  },
  low: {
    label: "Low",
    className:
      "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  },
};

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  waiting: {
    label: "Waiting",
    className:
      "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300",
  },
  contacted: {
    label: "Contacted",
    className:
      "bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300",
  },
  admitted: {
    label: "Admitted",
    className:
      "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300",
  },
  removed: {
    label: "Removed",
    className:
      "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  },
  expired: {
    label: "Expired",
    className:
      "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  },
};

function Badge({
  config,
  value,
}: {
  config: Record<string, { label: string; className: string }>;
  value: string;
}) {
  const c = config[value] || { label: value, className: "bg-gray-100 text-gray-600" };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        c.className,
      )}
    >
      {c.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Add Entry Form
// ---------------------------------------------------------------------------

function AddEntryForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [priority, setPriority] = useState(0);
  const [notes, setNotes] = useState("");

  const addMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post("/shelter/waiting-list", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["waitingList"] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addMutation.mutate({
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      phone: phone || undefined,
      priority,
      notes: notes || undefined,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Add to Waiting List</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
            />
            <Input
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
            />
          </div>

          <Input
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone number"
          />

          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none text-foreground">
              Priority
            </label>
            <select
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value={0}>Low</option>
              <option value={1}>Medium</option>
              <option value={2}>High</option>
              <option value={3}>Urgent</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none text-foreground">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Additional notes..."
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={addMutation.isPending}>
              <Plus className="h-4 w-4" />
              Add Entry
            </Button>
          </div>

          {addMutation.isError && (
            <p className="text-sm text-destructive">
              Failed to add entry. Please try again.
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// WaitingList Component
// ---------------------------------------------------------------------------

export function WaitingList() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("waiting");
  const [showAddForm, setShowAddForm] = useState(false);

  const { data, isLoading, error } = useQuery<WaitingListResponse>({
    queryKey: ["waitingList", statusFilter],
    queryFn: () => {
      const params: Record<string, string> = {};
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      return api.get<WaitingListResponse>("/shelter/waiting-list", { params });
    },
  });

  const admitMutation = useMutation({
    mutationFn: (id: number) =>
      api.post(`/shelter/waiting-list/${id}/admit`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["waitingList"] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) =>
      api.delete(`/shelter/waiting-list/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["waitingList"] });
    },
  });

  const entries = data?.data || [];
  const total = data?.meta?.total || 0;

  const statusTabs: { value: StatusFilter; label: string }[] = [
    { value: "waiting", label: "Waiting" },
    { value: "contacted", label: "Contacted" },
    { value: "admitted", label: "Admitted" },
    { value: "removed", label: "Removed" },
    { value: "all", label: "All" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Waiting List</h1>
          <p className="text-sm text-muted-foreground">
            {total} {total === 1 ? "entry" : "entries"}
            {statusFilter !== "all" ? ` (${statusFilter})` : ""}
          </p>
        </div>
        <Button onClick={() => setShowAddForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add to List
        </Button>
      </div>

      {/* Add Form */}
      {showAddForm && <AddEntryForm onClose={() => setShowAddForm(false)} />}

      {/* Status filter tabs */}
      <div className="flex gap-1 rounded-lg bg-muted p-1">
        {statusTabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              statusFilter === tab.value
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load waiting list. Please try again.
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && entries.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Clock className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="text-lg font-medium text-foreground">
              No entries found
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {statusFilter === "waiting"
                ? "The waiting list is empty."
                : `No ${statusFilter} entries.`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {!isLoading && entries.length > 0 && (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Phone
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  <span className="flex items-center gap-1">
                    Priority
                    <ArrowUpDown className="h-3 w-3" />
                  </span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Date Added
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Notes
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className="hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3 text-sm font-medium text-foreground">
                    {entry.firstName || entry.lastName
                      ? `${entry.firstName || ""} ${entry.lastName || ""}`.trim()
                      : "Walk-in"}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {entry.phone ? (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {entry.phone}
                      </span>
                    ) : (
                      "--"
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge config={PRIORITY_CONFIG} value={entry.priorityLabel} />
                  </td>
                  <td className="px-4 py-3">
                    <Badge config={STATUS_CONFIG} value={entry.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {entry.dateAdded}
                  </td>
                  <td className="max-w-[200px] truncate px-4 py-3 text-sm text-muted-foreground">
                    {entry.notes || "--"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {entry.status === "waiting" && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => admitMutation.mutate(entry.id)}
                            disabled={admitMutation.isPending}
                            title="Admit"
                          >
                            <UserCheck className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeMutation.mutate(entry.id)}
                            disabled={removeMutation.isPending}
                            title="Remove"
                          >
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        </>
                      )}
                      {entry.status === "contacted" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => admitMutation.mutate(entry.id)}
                          disabled={admitMutation.isPending}
                          title="Admit"
                        >
                          <UserCheck className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mutation errors */}
      {admitMutation.isError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          Failed to admit person. Please try again.
        </div>
      )}
      {removeMutation.isError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          Failed to remove entry. Please try again.
        </div>
      )}
    </div>
  );
}
